import { useEffect, useMemo, useState } from 'react';
import { subscriptionsService } from '../services/subscriptions';
import { useFetch } from '../hooks/useFetch';
import DataTable from '../components/DataTable';
import Spinner from '../components/Spinner';
import StatusMessage from '../components/StatusMessage';
import Modal from '../components/Modal';
import Field from '../components/Field';
import type { DurationDiscounts, Invoice, SubscriptionSettingUpdate } from '../types';

type DurationRow = { months: string; percent: string };

const rupiah = (n: number) => `Rp ${n.toLocaleString('id-ID')}`;

const formatDiscounts = (map: DurationDiscounts | undefined) => {
  if (!map || Object.keys(map).length === 0) return '—';
  return Object.entries(map)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([months, percent]) => `${months} mo: ${percent}%`)
    .join(' · ');
};

const invoiceTypeLabel = (type: Invoice['type']) => (type === 'quota_addition' ? 'Quota addition' : 'Renewal');

/** Parses the duration-row editor into a validated DurationDiscounts map, or an error message. */
function parseDurationRows(rows: DurationRow[]): { map: DurationDiscounts } | { error: string } {
  if (rows.length === 0) return { error: 'Duration discounts cannot be empty — add at least one duration.' };
  const map: DurationDiscounts = {};
  for (const row of rows) {
    const monthsStr = row.months.trim();
    const monthsNum = Number(monthsStr);
    if (!monthsStr || !Number.isInteger(monthsNum) || monthsNum <= 0) {
      return { error: `"${row.months || '(empty)'}" is not a valid duration — months must be a positive whole number.` };
    }
    const key = String(monthsNum);
    if (key in map) return { error: `Duration ${monthsNum} months is listed more than once.` };
    const percentStr = row.percent.trim();
    const percentNum = Number(percentStr);
    if (percentStr === '' || Number.isNaN(percentNum) || percentNum < 0 || percentNum > 100) {
      return { error: `Discount for ${monthsNum} months must be a percent between 0 and 100.` };
    }
    map[key] = percentNum;
  }
  return { map };
}

/**
 * Parses a required numeric text field. Deliberately does NOT treat an empty string as 0 —
 * Number('') === 0 in JS, which would let an admin clear a field mid-edit and silently submit
 * a zero price/limit if they hit Save before retyping. An empty field is "not filled in", and
 * blocks submit with a message instead.
 */
function parseRequiredNumber(raw: string, label: string, opts: { integer?: boolean; min?: number } = {}): { value: number } | { error: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { error: `${label} is required.` };
  const num = Number(trimmed);
  if (Number.isNaN(num)) return { error: `${label} must be a number.` };
  if (opts.integer && !Number.isInteger(num)) return { error: `${label} must be a whole number.` };
  if (opts.min !== undefined && num < opts.min) return { error: `${label} must be at least ${opts.min}.` };
  return { value: num };
}

const Subscriptions = () => {
  const [status, setStatus] = useState('');
  const invoices = useFetch(() => subscriptionsService.invoices(status || undefined), [status]);
  const settings = useFetch(() => subscriptionsService.getSettings(), []);
  const [edit, setEdit] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Held as strings (like the duration rows) so an in-progress edit — field cleared, not yet
  // retyped — can never collapse to Number('') === 0 and get submitted as a silent zero.
  const [stationPrice, setStationPrice] = useState('');
  const [addonUserPrice, setAddonUserPrice] = useState('');
  const [rows, setRows] = useState<DurationRow[]>([]);
  const [minMonths, setMinMonths] = useState('1');
  const [trialDays, setTrialDays] = useState('');
  const [trialUserLimit, setTrialUserLimit] = useState('');
  const [adminWhatsapp, setAdminWhatsapp] = useState('');
  const [pricePerUser, setPricePerUser] = useState(0);
  const [freeUserCount, setFreeUserCount] = useState(0);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankAccountHolder, setBankAccountHolder] = useState('');
  // The invoice awaiting confirmation, or null when the dialog is closed. Holding the whole
  // row (not just an id) lets the dialog restate what is being paid, so a mis-click on the
  // wrong table row is visible before it is committed rather than after.
  const [confirmInvoice, setConfirmInvoice] = useState<Invoice | null>(null);
  const [markingPaid, setMarkingPaid] = useState(false);

  const openEdit = () => {
    const d = settings.data;
    if (!d) return;
    setStationPrice(String(d.station_price));
    setAddonUserPrice(String(d.addon_user_price));
    setRows(
      Object.entries(d.duration_discounts)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([months, percent]) => ({ months, percent: String(percent) })),
    );
    setMinMonths(String(d.min_subscription_months));
    setTrialDays(String(d.trial_days));
    setTrialUserLimit(String(d.trial_user_limit));
    setAdminWhatsapp(d.admin_whatsapp);
    setPricePerUser(d.price_per_user);
    setFreeUserCount(d.free_user_count);
    setBankName(d.bank_name ?? '');
    setBankAccountNumber(d.bank_account_number ?? '');
    setBankAccountHolder(d.bank_account_holder ?? '');
    setMsg(null);
    setFieldErrors({});
    setLegacyOpen(false);
    setEdit(true);
  };

  const updateRow = (i: number, field: keyof DurationRow, value: string) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const addRow = () => setRows((rs) => [...rs, { months: '', percent: '' }]);
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  // Months this select may legally offer — must match the current duration-discount rows 1:1.
  const validMonths = useMemo(() => {
    const parsed = parseDurationRows(rows);
    return 'map' in parsed ? Object.keys(parsed.map).sort((a, b) => Number(a) - Number(b)) : [];
  }, [rows]);

  const fieldErr = (key: string): string | null => {
    const direct = fieldErrors[key] ?? [];
    const nested = Object.keys(fieldErrors)
      .filter((k) => k.startsWith(`${key}.`))
      .flatMap((k) => fieldErrors[k]);
    const all = [...direct, ...nested];
    return all.length ? all.join(' ') : null;
  };

  // The legacy fields live inside a collapsed <details> — auto-open it if the server put a
  // field error on either one, so a 422 there is never silently hidden behind the fold.
  const hasLegacyError = Boolean(fieldErr('price_per_user') || fieldErr('free_user_count'));
  useEffect(() => {
    if (hasLegacyError) setLegacyOpen(true);
  }, [hasLegacyError]);

  const saveSettings = async () => {
    setMsg(null);
    setFieldErrors({});

    const stationPriceParsed = parseRequiredNumber(stationPrice, 'Station price', { min: 0 });
    if ('error' in stationPriceParsed) {
      setMsg(stationPriceParsed.error);
      return;
    }
    const addonUserPriceParsed = parseRequiredNumber(addonUserPrice, 'Addon user price', { min: 0 });
    if ('error' in addonUserPriceParsed) {
      setMsg(addonUserPriceParsed.error);
      return;
    }
    const parsed = parseDurationRows(rows);
    if ('error' in parsed) {
      setMsg(parsed.error);
      return;
    }
    const minMonthsNum = Number(minMonths);
    if (!Number.isInteger(minMonthsNum) || !(String(minMonthsNum) in parsed.map)) {
      setMsg('Min subscription months must be one of the duration discount entries above.');
      return;
    }
    const trialDaysParsed = parseRequiredNumber(trialDays, 'Trial days', { integer: true, min: 0 });
    if ('error' in trialDaysParsed) {
      setMsg(trialDaysParsed.error);
      return;
    }
    const trialUserLimitParsed = parseRequiredNumber(trialUserLimit, 'Trial user limit', { integer: true, min: 0 });
    if ('error' in trialUserLimitParsed) {
      setMsg(trialUserLimitParsed.error);
      return;
    }

    // Only fields the server accepts — users_per_station is deliberately never sent (see note above).
    const payload: SubscriptionSettingUpdate = {
      station_price: stationPriceParsed.value,
      addon_user_price: addonUserPriceParsed.value,
      duration_discounts: parsed.map,
      min_subscription_months: minMonthsNum,
      trial_days: trialDaysParsed.value,
      trial_user_limit: trialUserLimitParsed.value,
      admin_whatsapp: adminWhatsapp,
      price_per_user: pricePerUser,
      free_user_count: freeUserCount,
      bank_name: bankName,
      bank_account_number: bankAccountNumber,
      bank_account_holder: bankAccountHolder,
    };

    try {
      await subscriptionsService.updateSettings(payload);
      setEdit(false);
      settings.reload();
    } catch (e) {
      const err = e as Error & { fields?: Record<string, string[]> };
      setMsg(err.message);
      setFieldErrors(err.fields ?? {});
    }
  };

  // Marking an invoice paid activates the subscription, moves valid_until and writes the
  // company's quota — and there is no unmark-paid endpoint, so it cannot be undone from
  // here. Hence the confirmation step: the click only opens the dialog.
  const markPaid = async () => {
    if (!confirmInvoice || markingPaid) return;
    setMsg(null);
    setMarkingPaid(true);
    try {
      await subscriptionsService.markPaid(confirmInvoice.id);
      setConfirmInvoice(null);
      invoices.reload();
    } catch (e) {
      setMsg((e as Error).message);
      // Deliberately keep the dialog open on failure: closing it would leave the error
      // stranded behind the table with nothing naming which invoice it belonged to.
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Subscriptions</h1>
      {msg && <StatusMessage error={msg} />}

      <StatusMessage error={settings.error} />
      <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
        <div className="flex items-start justify-between gap-4">
          {settings.loading ? (
            <div>Loading settings…</div>
          ) : settings.data ? (
            <div className="space-y-1">
              <div>
                Station price: <strong>{rupiah(settings.data.station_price)}/month</strong>
                {' · '}Addon user: <strong>{rupiah(settings.data.addon_user_price)}/month</strong>
              </div>
              <div className="text-gray-600">
                Includes {settings.data.users_per_station} employees per station
                <span className="text-gray-400"> (fixed — cannot be changed here, would retroactively evict paid seats on every active subscription)</span>
              </div>
              <div className="text-gray-600">Duration discounts: {formatDiscounts(settings.data.duration_discounts)}</div>
              <div className="text-gray-600">
                Min subscription: {settings.data.min_subscription_months} months · Trial: {settings.data.trial_days} days (up to{' '}
                {settings.data.trial_user_limit} users) · Admin WhatsApp: {settings.data.admin_whatsapp || '—'}
              </div>
              <div className="text-gray-600">
                Destination bank account: {settings.data.bank_name || '—'}
                {' · '}
                {settings.data.bank_account_number || '—'}
                {' · '}
                {settings.data.bank_account_holder || '—'}
              </div>
              <div className="text-xs text-gray-400">
                Legacy (unused, kept for old clients): price_per_user {rupiah(settings.data.price_per_user)}, free_user_count{' '}
                {settings.data.free_user_count}
              </div>
            </div>
          ) : null}
          <button className="text-primary-600 shrink-0" onClick={openEdit} disabled={!settings.data}>
            Edit
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All invoices</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <StatusMessage error={invoices.error} />
      {invoices.loading ? (
        <Spinner />
      ) : (
        <DataTable<Invoice>
          columns={[
            { key: 'company_name', header: 'Company', render: (r) => r.company_name ?? '—' },
            { key: 'created_at', header: 'Date', render: (r) => new Date(r.created_at).toLocaleDateString('id-ID') },
            { key: 'type', header: 'Type', render: (r) => invoiceTypeLabel(r.type) },
            { key: 'months', header: 'Months', render: (r) => (r.type === 'quota_addition' || r.months === 0 ? '—' : r.months) },
            { key: 'user_count', header: 'Seats purchased', render: (r) => r.user_count },
            { key: 'amount', header: 'Amount', render: (r) => rupiah(r.amount) },
            { key: 'status', header: 'Status' },
            {
              key: 'action',
              header: '',
              render: (r) => (r.status === 'pending' ? <button className="text-primary-600 text-sm" onClick={() => setConfirmInvoice(r)}>Mark paid</button> : null),
            },
          ]}
          rows={invoices.data ?? []}
        />
      )}

      <Modal open={edit} title="Subscription settings" onClose={() => setEdit(false)}>
        <div className="space-y-1 text-sm max-h-[70vh] overflow-y-auto pr-1">
          {settings.data && (
            <div className="text-xs text-gray-400 mb-2">
              Employees per station: {settings.data.users_per_station} — fixed, not editable here.
            </div>
          )}

          <Field label="Station price (Rp/month)">
            <input type="number" className="w-full border rounded px-3 py-2" value={stationPrice} onChange={(e) => setStationPrice(e.target.value)} />
            {fieldErr('station_price') && <div className="text-red-600 text-xs mt-1">{fieldErr('station_price')}</div>}
          </Field>

          <Field label="Addon user price (Rp/month/user)">
            <input type="number" className="w-full border rounded px-3 py-2" value={addonUserPrice} onChange={(e) => setAddonUserPrice(e.target.value)} />
            {fieldErr('addon_user_price') && <div className="text-red-600 text-xs mt-1">{fieldErr('addon_user_price')}</div>}
          </Field>

          <div className="mb-3">
            <span className="block text-xs text-gray-500 mb-1">Duration discounts (months → percent)</span>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="number"
                    aria-label={`Duration months row ${i + 1}`}
                    className="w-24 border rounded px-2 py-1"
                    placeholder="months"
                    value={row.months}
                    onChange={(e) => updateRow(i, 'months', e.target.value)}
                  />
                  <span className="text-gray-400">mo →</span>
                  <input
                    type="number"
                    aria-label={`Duration percent row ${i + 1}`}
                    className="w-20 border rounded px-2 py-1"
                    placeholder="%"
                    value={row.percent}
                    onChange={(e) => updateRow(i, 'percent', e.target.value)}
                  />
                  <span className="text-gray-400">%</span>
                  <button type="button" className="text-red-500 text-xs ml-auto" onClick={() => removeRow(i)}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="text-primary-600 text-xs" onClick={addRow}>
                + Add duration
              </button>
            </div>
            {fieldErr('duration_discounts') && <div className="text-red-600 text-xs mt-1">{fieldErr('duration_discounts')}</div>}
          </div>

          <Field label="Min subscription months">
            <select className="w-full border rounded px-3 py-2" value={minMonths} onChange={(e) => setMinMonths(e.target.value)}>
              {!validMonths.includes(minMonths) && minMonths && <option value={minMonths}>{minMonths} (not in duration discounts — invalid)</option>}
              {validMonths.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {fieldErr('min_subscription_months') && <div className="text-red-600 text-xs mt-1">{fieldErr('min_subscription_months')}</div>}
          </Field>

          <Field label="Trial days">
            <input type="number" className="w-full border rounded px-3 py-2" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
            {fieldErr('trial_days') && <div className="text-red-600 text-xs mt-1">{fieldErr('trial_days')}</div>}
          </Field>

          <Field label="Trial user limit">
            <input type="number" className="w-full border rounded px-3 py-2" value={trialUserLimit} onChange={(e) => setTrialUserLimit(e.target.value)} />
            {fieldErr('trial_user_limit') && <div className="text-red-600 text-xs mt-1">{fieldErr('trial_user_limit')}</div>}
          </Field>

          <Field label="Admin WhatsApp">
            <input type="text" className="w-full border rounded px-3 py-2" value={adminWhatsapp} onChange={(e) => setAdminWhatsapp(e.target.value)} />
            {fieldErr('admin_whatsapp') && <div className="text-red-600 text-xs mt-1">{fieldErr('admin_whatsapp')}</div>}
          </Field>

          <Field label="Destination bank name">
            <input type="text" className="w-full border rounded px-3 py-2" value={bankName} onChange={(e) => setBankName(e.target.value)} />
            {fieldErr('bank_name') && <div className="text-red-600 text-xs mt-1">{fieldErr('bank_name')}</div>}
          </Field>

          <Field label="Destination bank account number">
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={bankAccountNumber}
              onChange={(e) => setBankAccountNumber(e.target.value)}
            />
            {fieldErr('bank_account_number') && <div className="text-red-600 text-xs mt-1">{fieldErr('bank_account_number')}</div>}
          </Field>

          <Field label="Destination bank account holder">
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={bankAccountHolder}
              onChange={(e) => setBankAccountHolder(e.target.value)}
            />
            {fieldErr('bank_account_holder') && <div className="text-red-600 text-xs mt-1">{fieldErr('bank_account_holder')}</div>}
          </Field>

          <details className="border rounded px-3 py-2" open={legacyOpen} onToggle={(e) => setLegacyOpen((e.target as HTMLDetailsElement).open)}>
            <summary className="text-xs text-gray-500 cursor-pointer">Legacy fields (unused, kept for old clients)</summary>
            <div className="mt-2 space-y-3">
              <Field label="price_per_user (Rp) — legacy">
                <input type="number" className="w-full border rounded px-3 py-2" value={pricePerUser} onChange={(e) => setPricePerUser(Number(e.target.value))} />
                {fieldErr('price_per_user') && <div className="text-red-600 text-xs mt-1">{fieldErr('price_per_user')}</div>}
              </Field>
              <Field label="free_user_count — legacy">
                <input type="number" className="w-full border rounded px-3 py-2" value={freeUserCount} onChange={(e) => setFreeUserCount(Number(e.target.value))} />
                {fieldErr('free_user_count') && <div className="text-red-600 text-xs mt-1">{fieldErr('free_user_count')}</div>}
              </Field>
            </div>
          </details>

          <button onClick={saveSettings} className="bg-primary-600 text-white rounded px-4 py-2 w-full mt-3">
            Save
          </button>
        </div>
      </Modal>

      <Modal open={!!confirmInvoice} title="Mark invoice paid" onClose={() => !markingPaid && setConfirmInvoice(null)}>
        {confirmInvoice && (
          <div className="space-y-3 text-sm">
            <div className="space-y-1">
              <div>
                Company: <b>{confirmInvoice.company_name ?? '—'}</b>
              </div>
              <div>Type: {invoiceTypeLabel(confirmInvoice.type)}</div>
              {/* A quota addition carries months = 0 and never moves valid_until, so printing
                  "0 months" next to it would read as a duration rather than as its absence. */}
              {confirmInvoice.type !== 'quota_addition' && confirmInvoice.months > 0 && (
                <div>Duration: {confirmInvoice.months} months</div>
              )}
              <div>
                Quota after payment: {confirmInvoice.station_quota} stations, {confirmInvoice.addon_user_quota} addon employees
              </div>
              <div className="text-base">
                Amount: <b>{rupiah(confirmInvoice.amount)}</b>
              </div>
              <div className="text-xs text-gray-500">
                Invoice created {new Date(confirmInvoice.created_at).toLocaleDateString('id-ID')}
              </div>
            </div>
            {/* No resulting end date is shown on purpose: period_end is written by the server
                when the invoice is paid, so it is null here. Computing it in the browser would
                mean stating a date nothing has confirmed. */}
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
              This activates the subscription and writes the company's quota. It cannot be undone from this
              panel — check the amount against the bank statement first.
            </div>
            {/* The page-level StatusMessage sits behind this overlay, so a failure would be
                invisible while the dialog stays open. Repeat it where the operator is looking. */}
            <StatusMessage error={msg} />
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmInvoice(null)}
                disabled={markingPaid}
                className="border rounded px-4 py-2 text-sm w-full disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={markPaid}
                disabled={markingPaid}
                className="bg-red-600 text-white rounded px-4 py-2 text-sm w-full disabled:opacity-50"
              >
                {markingPaid ? 'Marking…' : 'Yes, mark paid'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
export default Subscriptions;
